<!-- eslint-disable vue/no-v-html -->
<template>
  <div class="filter-section">
    <div
      class="filter-section-header"
      :class="{ 'cursor-pointer': isExpandable }"
      @click="$emit('toggle-expand')"
    >
      <span v-if="title" class="font-subtitle-2-strong">{{ title }}</span>
      <span
        v-else-if="computedTitleWithDefaultName"
        class="font-subtitle-2-strong"
      >
        {{ $t("appliedFilters.defaultFilterFor") }}
        <span class="font-medium">
          {{ savedSearchStore.selectedSearch.name }}
        </span>
      </span>
      <span v-else class="font-subtitle-2-strong"> {{ computedTitle }} </span>
      <el-tooltip
        v-if="tooltipText !== null"
        placement="left"
        popper-class="applied-filters-tooltip font-body-3"
      >
        <template #content>
          <span v-html="tooltipText"></span>
        </template>
        <font-awesome-icon
          class="info-tooltip focus:outline-none"
          :icon="['far', 'info-circle']"
        />
      </el-tooltip>
      <font-awesome-icon
        v-if="isExpandable"
        :data-intercom-target="`${dataTarget}`"
        class="outline-none cursor-pointer"
        :icon="['fa-solid', icon]"
      />
      <button
        v-if="showDefaultSearchFilterButton"
        class="default-search-filter font-subtitle-2"
        :class="{ isActive: !isDefaultSearchFilterSelected }"
        :disabled="isDefaultSearchFilterSelected"
        @click="searchFilterStore.setCurrentSearchFilter(null)"
      >
        <font-awesome-icon
          class="mr-2"
          :icon="['far', 'arrow-rotate-right']"
        />{{ $t("appliedFilters.backToDefault") }}
      </button>
    </div>
    <el-collapse-transition>
      <div v-show="isAlwaysExpanded || isExpanded" class="filter-section-body">
        <slot />
      </div>
    </el-collapse-transition>
  </div>
</template>

<script setup lang="ts">
import { Ref, ref, computed, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useSearchFiltersStore } from "~/store/searchFilter";
import { useSearchStore } from "~/store/search";

const { t } = useI18n();
const searchFilterStore = useSearchFiltersStore();
const savedSearchStore = useSearchStore();

const props = defineProps({
  isAlwaysExpanded: {
    type: Boolean,
    default: true,
  },
  title: {
    type: String,
    default: null,
  },
  tooltipText: {
    type: String,
    default: null,
  },
  showDefaultSearchFilterButton: {
    type: Boolean,
    default: false,
  },
  dataTarget: {
    type: String,
    default: "",
  },
  isExpanded: {
    type: Boolean,
    default: false,
  },
});

defineEmits(["toggle-expand"]);

const icon: Ref<string> = ref("chevron-down");

const isDefaultSearchFilterSelected = computed(() => {
  return !searchFilterStore.currentSearchFilter?.hasOwnProperty("id");
});

const isExpandable = computed(() => {
  return props.title && props.isAlwaysExpanded === false;
});

const computedTitleWithDefaultName = computed(() => {
  return (
    searchFilterStore.isCurrentFilterDefault &&
    savedSearchStore?.selectedSearch?.name
  );
});

const computedTitle = computed(() => {
  if (
    searchFilterStore.isCurrentFilterDefault &&
    Object.keys(savedSearchStore?.selectedSearch).length === 0
  ) {
    return t("appliedFilters.defaultFilter");
  } else {
    return t("appliedFilters.appliedFilter");
  }
});

watch(
  () => props.isExpanded,
  (newVal) => {
    icon.value = newVal ? "chevron-up" : "chevron-down";
  },
);
</script>

<style scoped>
.filter-section {
  @apply mt-4 border border-accent-200 rounded-[0.25rem] px-[10px] py-[14px];
}
.filter-section-header {
  @apply flex justify-between items-center;
}
.info-tooltip {
  @apply w-4 h-4 rounded-full text-secondary-600;
}
.default-search-filter {
  @apply text-secondary-600 !important;
}
.isActive {
  @apply text-success-400;
}
.filter-section-body {
  @apply pt-[10px];
}
</style>

<style>
.applied-filters-tooltip {
  @apply w-[274px] text-white text-sm font-normal font-['Inter'] leading-tight !important;
}
</style>
